// src/modules/settings/helpers/rangeValidation.ts

/**
 * Parsed range type
 */
export type ParsedRange = {
  type: 'gt' | 'gte' | 'lt' | 'lte' | 'range';
  value: number;
  value2?: number;
} | null;

/**
 * Parses range strings into structured format
 * Supports formats: >90, >=60, <30, <=20, 61-90
 * @param rangeStr - Range string to parse
 * @returns Parsed range object or null if invalid
 */
export const parseRange = (rangeStr: string): ParsedRange => {
  const gtMatch = rangeStr.match(/^>(\d+(?:\.\d+)?)$/);
  const gteMatch = rangeStr.match(/^>=(\d+(?:\.\d+)?)$/);
  const ltMatch = rangeStr.match(/^<(\d+(?:\.\d+)?)$/);
  const lteMatch = rangeStr.match(/^<=(\d+(?:\.\d+)?)$/);
  const rangeMatch = rangeStr.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);

  if (gtMatch) return { type: 'gt', value: parseFloat(gtMatch[1]) };
  if (gteMatch) return { type: 'gte', value: parseFloat(gteMatch[1]) };
  if (ltMatch) return { type: 'lt', value: parseFloat(ltMatch[1]) };
  if (lteMatch) return { type: 'lte', value: parseFloat(lteMatch[1]) };
  if (rangeMatch) return { type: 'range', value: parseFloat(rangeMatch[1]), value2: parseFloat(rangeMatch[2]) };

  return null;
};

export const formatMinutesReference = (input: number | string): string => {
  const total = Number(input);
  if (!Number.isFinite(total) || total < 0) return 'Invalid';

  if (total === 0) return '0 minutes';

  const MONTH = 60 * 24 * 30; // approx
  const WEEK = 60 * 24 * 7;
  const DAY = 60 * 24;
  const HOUR = 60;

  let remaining = Math.floor(total);
  const parts: string[] = [];

  const pushPart = (value: number, label: string) => {
    if (value > 0) parts.push(`${value} ${label}${value > 1 ? 's' : ''}`);
  };

  const months = Math.floor(remaining / MONTH);
  remaining %= MONTH;

  const weeks = Math.floor(remaining / WEEK);
  remaining %= WEEK;

  const days = Math.floor(remaining / DAY);
  remaining %= DAY;

  const hours = Math.floor(remaining / HOUR);
  remaining %= HOUR;

  const minutes = remaining;

  pushPart(months, 'month');
  pushPart(weeks, 'week');
  pushPart(days, 'day');
  pushPart(hours, 'hour');
  pushPart(minutes, 'minute');

  // Keep it short for UI
  return parts.slice(0, 2).join(' ');
};
